/**
 * Close something open -- a menu -- the way a menu closes everywhere else: on a press
 * anywhere outside it, and on Escape.
 */

import { useEffect } from "react";
import type { RefObject } from "react";

/** While `open`, call `close` on a mouse press outside `inside` or on the Escape key. */
export function useDismiss(inside: RefObject<HTMLElement>, open: boolean, close: () => void): void {
  useEffect(() => {
    if (!open) return undefined;
    const away = (event: MouseEvent): void => {
      if (!(event.target instanceof Node) || inside.current?.contains(event.target) !== true) {
        close();
      }
    };
    const escape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      // Said, so a desk filling the window leaves it on the next Escape, not this one.
      event.preventDefault();
      close();
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [inside, open, close]);
}
